/**
 * LooksLab — SEO Configuration
 *
 * HOW TO USE:
 *   - Edit `site` for global brand/domain settings
 *   - Edit `defaults` to change the fallback applied to every page
 *   - Edit `pages.home` / `pages.catalog` for static page overrides
 *   - Edit `peptideDefaults` for the fallback applied to all peptide detail pages
 *   - `getPeptideSeo(product)` auto-generates per-peptide SEO from the product config;
 *     override any field here for a specific peptide by checking product.id
 *
 * ATTRIBUTES SUPPORTED PER PAGE:
 *   title          — <title> tag  (appends site.titleSuffix unless titleRaw: true)
 *   titleRaw       — set true to use title exactly as written, no suffix appended
 *   description    — <meta name="description">
 *   keywords       — <meta name="keywords">
 *   robots         — <meta name="robots">  e.g. "index, follow" | "noindex, nofollow"
 *   canonical      — <link rel="canonical">
 *   og.type        — og:type  e.g. "website" | "product" | "article"
 *   og.title       — og:title  (falls back to title)
 *   og.description — og:description  (falls back to description)
 *   og.image       — og:image  full URL
 *   og.imageAlt    — og:image:alt
 *   og.imageWidth  — og:image:width
 *   og.imageHeight — og:image:height
 *   og.url         — og:url  (falls back to canonical)
 *   og.siteName    — og:site_name  (falls back to site.name)
 *   og.locale      — og:locale  (falls back to site.locale)
 *   twitter.card        — twitter:card  e.g. "summary_large_image"
 *   twitter.site        — twitter:site  @handle  (falls back to site.twitterHandle)
 *   twitter.title       — twitter:title  (falls back to og.title → title)
 *   twitter.description — twitter:description  (falls back to og.description → description)
 *   twitter.image       — twitter:image  (falls back to og.image)
 *   twitter.imageAlt    — twitter:image:alt
 *   twitter.creator     — twitter:creator  @handle
 *   jsonLd         — JSON-LD structured data object (injected as <script type="application/ld+json">)
 */

// ─────────────────────────────────────────────────────────────
// 1. SITE-WIDE SETTINGS
// ─────────────────────────────────────────────────────────────
export const site = {
  name: 'LooksLab',
  siteUrl: 'https://lookslab.shop',
  titleSuffix: ' | LooksLab',           // appended to every page title
  locale: 'en_US',
  twitterHandle: '@lookslab',           // update with Twitter handle
  defaultOgImage: 'https://lookslab.shop/og-image.png',
  defaultOgImageAlt: 'LooksLab — Research-Grade Peptides',
  defaultOgImageWidth: '1200',
  defaultOgImageHeight: '630',
}

// ─────────────────────────────────────────────────────────────
// 2. GLOBAL DEFAULTS (applied to every page unless overridden)
// ─────────────────────────────────────────────────────────────
export const defaults = {
  title: 'Research-Grade Peptides',
  description:
    'Pure research peptides including BPC-157, Retatrutide, Tirzepatide, GHK-CU and more. ' +
    'Verified purity. Properly stored and dispatched. Not for human use. Not FDA approved.',
  keywords:
    'research peptides, BPC-157, retatrutide, tirzepatide, GHK-CU, TB-4, MT-2, lyophilized peptides, laboratory compounds',
  robots: 'index, follow',
  canonical: site.siteUrl,
  og: {
    type: 'website',
    siteName: site.name,
    locale: site.locale,
    image: site.defaultOgImage,
    imageAlt: site.defaultOgImageAlt,
    imageWidth: site.defaultOgImageWidth,
    imageHeight: site.defaultOgImageHeight,
  },
  twitter: {
    card: 'summary_large_image',
    site: site.twitterHandle,
  },
  jsonLd: null,
}

// ─────────────────────────────────────────────────────────────
// 3. STATIC PAGE SEO
// ─────────────────────────────────────────────────────────────
export const pages = {
  home: {
    title: 'Peptides That Ascend',
    titleRaw: false,
    description:
      'Your biology is a protocol. Rewrite it. LooksLab compounds pure research peptides — ' +
      'BPC-157, Retatrutide, Tirzepatide, GHK-CU, and more. Not for human use.',
    keywords:
      'lookslab, peptides that ascend, research peptides, BPC-157, retatrutide, tirzepatide, lyophilized',
    robots: 'index, follow',
    canonical: `${site.siteUrl}/`,
    og: {
      type: 'website',
      title: 'LooksLab — Peptides That Ascend',
      description:
        'Your biology is a protocol. Pure research peptides for scientific investigation.',
      image: site.defaultOgImage,
      imageAlt: 'LooksLab — Peptides That Ascend',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'LooksLab — Peptides That Ascend',
      description: 'Your biology is a protocol. Pure research peptides.',
    },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'LooksLab',
      url: site.siteUrl,
      logo: `${site.siteUrl}/logos/lookslab-logo.png`,
      description:
        'LooksLab compounds pure, research-grade peptides for scientific investigation.',
      sameAs: [],   // ← add social profile URLs here e.g. 'https://twitter.com/lookslab'
    },
  },

  catalog: {
    title: 'Full Catalog',
    description:
      'Browse the full LooksLab catalog of research-grade peptide compounds. ' +
      'Vials, pharmaceuticals, and research sequences. Purity-verified. Not for human use.',
    keywords:
      'peptide catalog, research compounds, BPC-157, retatrutide, tirzepatide, GHK-CU, TB-4, MT-2, semax, selank',
    robots: 'index, follow',
    canonical: `${site.siteUrl}/catalog`,
    og: {
      type: 'website',
      title: 'Full Peptide Catalog — LooksLab',
      description:
        'Browse the full LooksLab catalog of research-grade peptide compounds.',
    },
    twitter: {
      title: 'Full Peptide Catalog — LooksLab',
      description: 'Browse the full LooksLab research peptide catalog.',
    },
    jsonLd: null,
  },
}

// ─────────────────────────────────────────────────────────────
// 4. PEPTIDE DETAIL PAGE DEFAULTS
//    Applied to every /peptides/:id page before product-specific overrides
// ─────────────────────────────────────────────────────────────
export const peptideDefaults = {
  robots: 'index, follow',
  og: {
    type: 'product',
    image: site.defaultOgImage,
    imageAlt: site.defaultOgImageAlt,
    imageWidth: site.defaultOgImageWidth,
    imageHeight: site.defaultOgImageHeight,
  },
  twitter: {
    card: 'summary_large_image',
  },
}

// ─────────────────────────────────────────────────────────────
// 5. PER-PEPTIDE SEO GENERATOR
//    Called with the full product config object.
//    Add `if (product.id === 'retatrutide') { ... }` blocks here
//    to override specific fields for individual peptides.
// ─────────────────────────────────────────────────────────────
export function getPeptideSeo(product) {
  if (!product) return {}

  const priceRange = product.variants?.length
    ? product.variants.reduce(
        (acc, v) => {
          if (v.price < acc.min) acc.min = v.price
          if (v.price > acc.max) acc.max = v.price
          return acc
        },
        { min: Infinity, max: -Infinity },
      )
    : null

  const seo = {
    title: product.name,
    description:
      product.description ||
      `${product.name} — research-grade compound. Rigorously formulated and purity-verified. ` +
        `Available in ${product.variants?.map((v) => v.label).join(', ')} sizes. Not for human use.`,
    keywords: [
      product.name,
      product.subtitle,
      'research peptide',
      'laboratory compound',
      'lyophilized',
      'laboratory grade',
    ]
      .filter(Boolean)
      .join(', '),
    robots: 'index, follow',
    canonical: `${site.siteUrl}/peptides/${product.id}`,
    og: {
      type: 'product',
      title: `${product.name} — LooksLab Research Peptide`,
      description:
        product.description ||
        `${product.name} research-grade peptide. Not for human use.`,
    },
    twitter: {
      title: `${product.name} — LooksLab`,
      description:
        product.description || `${product.name} research peptide.`,
    },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      brand: {
        '@type': 'Brand',
        name: 'LooksLab',
      },
      ...(priceRange && priceRange.min !== Infinity
        ? {
            offers: {
              '@type': 'AggregateOffer',
              priceCurrency: 'USD',
              lowPrice: priceRange.min,
              highPrice: priceRange.max,
              offerCount: product.variants?.length ?? 1,
              availability:
                product.merchandise?.stock === 'in_stock'
                  ? 'https://schema.org/InStock'
                  : 'https://schema.org/OutOfStock',
            },
          }
        : {}),
    },
  }

  // ── Per-product overrides ───────────────────────────────────
  // Uncomment and edit to customise any individual peptide's SEO.
  //
  // if (product.id === 'retatrutide') {
  //   seo.title = 'Retatrutide (RT-3) — Triple GLP Agonist Research Compound'
  //   seo.description = 'Retatrutide RT-3 triple receptor agonist. Research use only.'
  //   seo.keywords += ', RT-3, triple agonist, GLP-3, GIP, GLP-1'
  // }
  //
  // if (product.id === 'bpc157') {
  //   seo.description = 'BPC-157 body protection compound. 5mg and 10mg vials. Research use only.'
  // }

  return seo
}

// ─────────────────────────────────────────────────────────────
// 6. ADMIN OVERRIDE MERGE
//    Applies a saved admin override (ProductsContext seoSettings.pages.X or
//    seoSettings.products[id] — see AdminPage SeoTab) on top of a base SEO
//    config. Blank override fields fall back to the base value. og.image
//    also backs twitter:image — SEO.jsx already falls back tw.image ?? og.image.
// ─────────────────────────────────────────────────────────────
export function applySeoOverrides(base, ov) {
  if (!ov) return base
  return {
    ...base,
    ...(ov.title       ? { title:       ov.title       } : {}),
    ...(ov.description ? { description: ov.description } : {}),
    ...(ov.keywords    ? { keywords:    ov.keywords    } : {}),
    ...(ov.robots      ? { robots:      ov.robots      } : {}),
    ...(ov.canonical   ? { canonical:   ov.canonical   } : {}),
    og: {
      ...base.og,
      ...(ov.ogTitle       ? { title:       ov.ogTitle       } : {}),
      ...(ov.ogDescription ? { description: ov.ogDescription } : {}),
      ...(ov.ogImage       ? { image:       ov.ogImage       } : {}),
    },
    twitter: {
      ...base.twitter,
      ...(ov.twitterTitle ? { title:       ov.twitterTitle } : {}),
      ...(ov.twitterDesc  ? { description: ov.twitterDesc  } : {}),
    },
  }
}
